import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { cn } from "@/lib/utils"
import { useReprocessAllStoresMutation } from "@/services/store-conciliation"
import { useGetJobProgressQuery } from "@/services/store/store"
import { useJobProgress } from "@/context/job-progress-context"
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2, RefreshCw, RotateCwSquare, Eye } from "lucide-react"
import { useState, useEffect } from "react"
import { toast } from "@/components/ui/use-toast"
import { FetchBaseQueryError } from "@reduxjs/toolkit/query"

type ProcessOption = "OPEN" | "ALL" | "UNLISTED" | null

const CONFIRMATION_TEXT: Record<Exclude<ProcessOption, null>, { title: string; description: string }> = {
    OPEN: {
        title: "Todos os itens em Aberto",
        description: "As lojas com status ABERTO e as que não vieram no lote do dia serão reprocessadas. As FECHADAS não serão tocadas.",
    },
    ALL: {
        title: "Todos os itens",
        description: "Todas as lojas serão reprocessadas, incluindo as com status FECHADO.",
    },
    UNLISTED: {
        title: "Apenas as lojas não listadas",
        description: "Somente as lojas que ainda não foram listadas serão processadas. As lojas já listadas serão preservadas integralmente.",
    },
}

interface CronJobButtonProps {
    conciliationDate: string;
}

/**
 * Helper para verificar se é erro 409 (Conflict)
 */
const isConflictError = (error: unknown): boolean => {
    if (!error) return false
    const fetchError = error as FetchBaseQueryError
    return fetchError.status === 409
}

export const ReprocessStoresModal = ({ conciliationDate }: CronJobButtonProps) => {
    const [optionDialogOpen, setOptionDialogOpen] = useState(false)
    const [confirmation, setConfirmation] = useState<{ show: boolean, option: ProcessOption }>({
        show: false,
        option: null,
    })
    const [selectedOption, setSelectedOption] = useState<ProcessOption>(null)

    const [trigger, { isLoading: isProcessing }] = useReprocessAllStoresMutation()

    // Contexto global para polling de progresso
    const { startPolling } = useJobProgress()

    // Verificar job ativo ao abrir modal
    const {
        data: activeJob,
        isLoading: isLoadingJobProgress,
        refetch: refetchJobProgress
    } = useGetJobProgressQuery(undefined, {
        skip: !optionDialogOpen,
    })

    // Refetch ao abrir modal
    useEffect(() => {
        if (optionDialogOpen) {
            refetchJobProgress()
        }
    }, [optionDialogOpen, refetchJobProgress])

    const hasActiveJob = activeJob && activeJob.status === 'RUNNING'

    const handleReprocessClick = () => setOptionDialogOpen(true)

    const handleOptionSelect = (option: ProcessOption) => setSelectedOption(option)

    const handleOptionConfirm = () => {
        if (!selectedOption) return
        setOptionDialogOpen(false)
        setConfirmation({ show: true, option: selectedOption })
    }

    const handleFinalConfirmation = async () => {
        if (!confirmation.option) return

        const reprocessAll = confirmation.option === "ALL"
        const onlyUnlisted = confirmation.option === "UNLISTED"

        try {
            const result = await trigger({ date: conciliationDate, reprocessAll, onlyUnlisted }).unwrap()

            // Fechar modal e iniciar polling
            setConfirmation({ show: false, option: null })
            setSelectedOption(null)

            if (!result.jobId) {
                toast({
                    title: "Nenhuma Loja Ausente",
                    description: "Todas as lojas já estão listadas. Nenhuma alteração foi feita.",
                    duration: 5000,
                })
                return
            }

            // Iniciar polling para acompanhar progresso
            console.log('[REPROCESS_MODAL] Trigger bem-sucedido, iniciando polling...')
            startPolling()
            console.log('[REPROCESS_MODAL] startPolling() chamado')

            toast({
                title: "Reprocessamento Iniciado",
                description: "O processo foi iniciado. Você pode acompanhar o progresso em tempo real.",
                duration: 5000,
            })
        } catch (err) {
            console.error("Erro ao reprocessar lojas:", err)

            // Verificar se é erro 409 (job já em andamento)
            if (isConflictError(err)) {
                toast({
                    title: "Job Já em Andamento",
                    description: "Outro usuário iniciou um job enquanto você preenchia o formulário.",
                    variant: "destructive",
                })
                // Atualizar UI para mostrar alert de job ativo
                refetchJobProgress()
                // Voltar para o primeiro modal para mostrar o alert
                setConfirmation({ show: false, option: null })
                setOptionDialogOpen(true)
            } else {
                toast({
                    title: "Erro",
                    description: "Falha ao iniciar o reprocessamento. Tente novamente.",
                    variant: "destructive",
                })
                setConfirmation({ show: false, option: null })
                setSelectedOption(null)
            }
        }
    }

    const handleCancel = () => {
        // Permitir fechar o modal de opções a qualquer momento (exceto durante o envio)
        if (isProcessing) return
        setOptionDialogOpen(false)
        setSelectedOption(null)
    }

    const handleConfirmationCancel = () => {
        // Permitir fechar o modal de confirmação a qualquer momento (exceto durante o envio)
        if (isProcessing) return
        setConfirmation({ show: false, option: null })
        setSelectedOption(null)
    }

    const handleViewProgress = () => {
        setOptionDialogOpen(false)
        startPolling()
    }

    // Handler para o onOpenChange do primeiro Dialog - sempre permitir fechar
    const handleOptionDialogChange = (open: boolean) => {
        if (!open) {
            // Fechando o modal - sempre permitir
            setOptionDialogOpen(false)
            setSelectedOption(null)
        } else {
            setOptionDialogOpen(true)
        }
    }

    return (
        <>
            <Button
                variant="outline"
                className="hover:text-white text-[#4B303C]"
                onClick={handleReprocessClick} disabled={isProcessing}
            >
                <RotateCwSquare />
                Reprocessar Lojas
            </Button>

            <Dialog open={optionDialogOpen} onOpenChange={handleOptionDialogChange}>
                <DialogContent
                    className="sm:max-w-md rounded-lg border-0 shadow-lg bg-white"
                    onInteractOutside={(e) => {
                        // Permitir fechar clicando fora
                        e.preventDefault()
                        handleOptionDialogChange(false)
                    }}
                    onEscapeKeyDown={() => handleOptionDialogChange(false)}
                >
                    <DialogHeader className="border-b pb-5">
                        <DialogTitle className="text-2xl font-bold text-[#2E0D1E] flex items-center gap-2">
                            <RefreshCw className="h-5 w-5" />
                            Reprocessar Lojas
                        </DialogTitle>
                        <DialogDescription className="text-base text-muted-foreground pt-1">
                            {hasActiveJob
                                ? "Há um job em andamento no momento."
                                : "Selecione quais itens você deseja reprocessar."}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Loading state */}
                    {isLoadingJobProgress && (
                        <div className="flex items-center justify-center p-8">
                            <Loader2 className="animate-spin h-8 w-8 text-primary" />
                        </div>
                    )}

                    {/* Alert de job ativo */}
                    {!isLoadingJobProgress && hasActiveJob && (
                        <Alert variant="default" className="border-amber-200 bg-amber-50">
                            <AlertCircle className="h-4 w-4 text-amber-600" />
                            <AlertTitle className="text-amber-800">Job em Andamento</AlertTitle>
                            <AlertDescription className="text-amber-700">
                                <p className="mb-2">
                                    Há um reprocessamento em andamento iniciado por{' '}
                                    <strong>{activeJob.initiatedBy?.userName || 'Usuário'}</strong>
                                    {activeJob.initiatedBy?.userEmail && (
                                        <> ({activeJob.initiatedBy.userEmail})</>
                                    )}.
                                </p>
                                <p className="mb-2">
                                    <strong>{activeJob.processedStores || 0}</strong> de{' '}
                                    <strong>{activeJob.totalStores || 0}</strong> lojas processadas
                                    ({activeJob.percentComplete || 0}%)
                                </p>
                                {activeJob.estimatedTimeRemaining && (
                                    <p className="text-sm">
                                        Tempo estimado: {activeJob.estimatedTimeRemaining}
                                    </p>
                                )}
                                <p className="text-sm mt-2 text-amber-600">
                                    Aguarde a conclusão para iniciar um novo job.
                                </p>
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Formulário de seleção (apenas quando não há job ativo) */}
                    {!isLoadingJobProgress && !hasActiveJob && (
                        <div className="py-1">
                            <RadioGroup value={selectedOption || ""} onValueChange={(v) => handleOptionSelect(v as ProcessOption)} className="space-y-4">
                                <div className={cn("flex items-start space-x-3 rounded-md border p-4 transition-all", selectedOption === "OPEN" ? "border-primary bg-primary/5" : "hover:border-primary/50")}>
                                    <RadioGroupItem value="OPEN" id="open" className="mt-1" />
                                    <div className="space-y-1.5">
                                        <Label htmlFor="open" className="text-base font-medium">
                                            Reprocessar apenas os itens em <strong>ABERTO</strong>
                                        </Label>
                                        <p className="text-sm text-muted-foreground">
                                            Serão reprocessadas as lojas com status ABERTO e também as que não vieram no
                                            lote do dia, ignorando as FECHADAS.
                                        </p>
                                    </div>
                                </div>
                                <div className={cn("flex items-start space-x-3 rounded-md border p-4 transition-all", selectedOption === "ALL" ? "border-primary bg-primary/5" : "hover:border-primary/50")}>
                                    <RadioGroupItem value="ALL" id="all" className="mt-1" />
                                    <div className="space-y-1.5">
                                        <Label htmlFor="all" className="text-base font-medium">
                                            Reprocessar <strong>TODOS</strong> os itens
                                        </Label>
                                        <p className="text-sm text-muted-foreground">
                                            Serão reprocessadas todas as lojas, independente do status atual.
                                        </p>
                                    </div>
                                </div>
                                <div className={cn("flex items-start space-x-3 rounded-md border p-4 transition-all", selectedOption === "UNLISTED" ? "border-primary bg-primary/5" : "hover:border-primary/50")}>
                                    <RadioGroupItem value="UNLISTED" id="unlisted" className="mt-1" />
                                    <div className="space-y-1.5">
                                        <Label htmlFor="unlisted" className="text-base font-medium">
                                            Reprocessar lojas <strong>NÃO LISTADAS</strong>
                                        </Label>
                                        <p className="text-sm text-muted-foreground">
                                            Serão processadas apenas as lojas que ainda não foram listadas, preservando as informações das lojas já carregadas.
                                        </p>
                                    </div>
                                </div>
                            </RadioGroup>
                        </div>
                    )}

                    <DialogFooter className="sm:justify-between gap-3">
                        <Button variant="outline" onClick={handleCancel} className="flex-1 sm:flex-none">
                            {hasActiveJob ? 'Fechar' : 'Cancelar'}
                        </Button>

                        {hasActiveJob ? (
                            <Button onClick={handleViewProgress} className="flex-1 sm:flex-none">
                                <Eye className="h-4 w-4 mr-2" />
                                Ver Progresso
                            </Button>
                        ) : (
                            <Button
                                onClick={handleOptionConfirm}
                                disabled={!selectedOption || isLoadingJobProgress}
                                className="flex-1 sm:flex-none"
                            >
                                Continuar
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={confirmation.show}
                onOpenChange={(open) => {
                    // Permitir fechar sempre que não estiver processando
                    if (!open && !isProcessing) {
                        handleConfirmationCancel();
                    }
                }}
            >
                <DialogContent
                    className="sm:max-w-md rounded-lg border-0 shadow-lg bg-white"
                    onInteractOutside={(e) => {
                        if (!isProcessing) {
                            e.preventDefault()
                            handleConfirmationCancel()
                        }
                    }}
                    onEscapeKeyDown={() => {
                        if (!isProcessing) {
                            handleConfirmationCancel()
                        }
                    }}
                >
                    <DialogHeader className="border-b pb-5">
                        <DialogTitle className="text-2xl font-bold text-[#2E0D1E] flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-amber-500" />
                            Confirmação
                        </DialogTitle>
                        <DialogDescription className="text-base text-muted-foreground pt-1">
                            Você tem certeza que deseja reprocessar:
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-1 space-y-4">
                        <div className="flex items-center justify-center p-6 bg-muted/30 rounded-lg border">
                            <div className="text-center">
                                <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
                                <p className="text-lg font-medium">
                                    {confirmation.option ? CONFIRMATION_TEXT[confirmation.option].title : ""}
                                </p>
                                <p className="text-sm text-muted-foreground mt-2">
                                    {confirmation.option ? CONFIRMATION_TEXT[confirmation.option].description : ""}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-blue-900">
                                <p className="font-medium">Processamento em segundo plano</p>
                                <p className="text-blue-700 mt-1">
                                    O reprocessamento será executado em background. Você poderá acompanhar o progresso em tempo real.
                                </p>
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="sm:justify-between gap-3">
                        <Button variant="outline" onClick={handleConfirmationCancel} disabled={isProcessing} className="flex-1 sm:flex-none">Cancelar</Button>
                        <Button onClick={handleFinalConfirmation} disabled={isProcessing} className="flex-1 sm:flex-none">
                            {isProcessing ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Iniciando...
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Confirmar
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
